/**
 * TypeScript interfaces for the PDE (Project Data Exchange) JSON structure.
 * Generated from the PDE schema definition (Project Data Exchange.json).
 *
 * The PDE is a JSON file exchanged between IRES and the ERP system (MDIS/X360).
 * Each section is a numbered array of objects.
 */

/** Section 0: Project-level metadata */
export interface PdeProjectInfo {
  projectIdIres: string;
  projectIdErp: string | null;
  projectTypeId: number;
  creationTimestamp: string;
  pdeSchemaVersion: string;
  infoConfigVersion: string;
  erpBoQVersion: string | null;
  senderId: string;
  projectStatus: number;
}

/** Section 1: Apartment entries */
export interface PdeApartment {
  apartmentNumber: number;
  contactPersonLastName: string | null;
  contactPersonFirstName: string | null;
  floor: string | null;
}

/** Section 2: Room entries */
export interface PdeRoom {
  apartmentNumber: number;
  roomNumber: number;
  roomName: string;
  note: string | null;
  numberOfAffectedWalls: string;
  numberOfWalls: string;
  roomWidth: number;
  roomLength: number;
  roomHeight: number;
  roomArea: number;
}

/**
 * Section 3: Consolidated Work Items (CWI).
 * Aggregated work items with billing item mappings.
 * Entries with `workItemId: null` are standalone billing items from the ERP.
 */
export interface PdeConsolidatedWorkItem {
  apartmentNumber: number;

  /**
   * Room the work item belongs to. Null when it is not tied to a single room, and absent
   * altogether in a PDE written before the field was added.
   */
  roomNumber: number | null;

  numberOfApartments: number;
  numberOfRooms: number | null;
  /**
   * Component. Wall lines carry the wall count since 2026-10-07 ("1 Wand", "4 Wände"), plain "Wand" before.
   * The ERP echoes it unchanged; IRES Project maps the count back to "Wand" when it matches the line on import.
   */
  component: string;
  /** IRES work item ID. Null for standalone billing items from ERP. */
  workItemId: string | null;
  /** LV billing item ID, set by ERP in Status 9 */
  billingItemId: string | null;
  /** Secondary billing item ID if CWI maps to multiple */
  billingItemId2: string | null;
  /** IRES work item description text */
  workItemText: string;
  /** ERP-edited billing item text. Only set if ERP modifies the original LV text. */
  billingItemTextErp: string | null;
  quantityIres: number | null;
  quantityErp: number | null;
  quantityCompleted: number | null;
  unit: string | null;
  phase: string;
  trade: string;
  appointmentErpId: string | null;
  serviceProviderIres: string | null;
  serviceProviderErp: string | null;
  serviceProviderCompleted: string | null;
  completedByTimestamp: string | null;
  completedByUserId: string | null;
}

/** Section 4: Materials */
export interface PdeMaterial {
  apartmentNumber: number;
  chapter: string;
  section: string | null;
  paragraph: string | null;
  asset: string | null;
  quantity: number | null;
  unit: string | null;
}

/** Section 5: Time estimates per technician */
export interface PdeTimeEstimate {
  technicianId: string | null;
  protectionHours: number | null;
  dismantlingHours: number | null;
  setupDryingHours: number | null;
  measurementHours: number | null;
  packupDryingHours: number | null;
  restorationHours: number | null;
}

/** Section 6: Time tracking entries */
export interface PdeTimeTracking {
  entryNumber: number;
  date: string;
  apartmentNumber: number;
  phase: string;
  userNumber: number;
  technicianId: string;
  startOfWork: string;
  endOfWork: string;
  travelTimeHours: number | null;
  leakDetectionHours: number | null;
  damageAssessmentHours: number | null;
  protectionHours: number | null;
  dismantlingHours: number | null;
  setupDryingHours: number | null;
  measurementHours: number | null;
  packupDryingHours: number | null;
  restorationHours: number | null;
  workDone: string | null;
  materialUsed: string | null;
}

/** Section 8: Device assignments */
export interface PdeDeviceAssignment {
  apartmentNumber: number;

  /**
   * Room the device is assigned to. Null when it is assigned to the apartment as a whole, and
   * absent altogether in a PDE written before IRES Project 2013, which did not export the room.
   */
  roomNumber: number | null;

  deviceAssignmentNumber: number;
  deviceType: string;
  deviceId: string;
  countingDeviceId: string | null;
  electricityMeterType: string | null;
  meterStart: number | null;
  meterEnd: number | null;
  consumption: string | null;
  fromDate: string | null;
  toDate: string | null;
  note: string | null;
}

/**
 * Section 10: Work Items (room-level detail).
 * Each entry is a specific work item on one component of a room; every wall is its own entry.
 */
export interface PdeWorkItem {
  apartmentNumber: number;
  roomNumber: number;
  /** Component; for a wall its name as the app shows it ("Wand A" with a sketch, "Wand 1" without). */
  component: string;
  /**
   * @deprecated Dropped by IRES Project on 2026-10-07 (the wall name moved into `component`);
   * only files written before that carry it, as the sketch's wall name ("Wand A") or null.
   */
  wallNumber?: string | null;
  workItemId: string;
  billingItemId: string | null;
  billingItemId2: string | null;
  workItemText: string;
  /** ERP-edited billing item text. Only set if ERP modifies the original LV text. */
  billingItemTextErp: string | null;
  quantityIres: number;
  quantityErp: number | null;
  quantityCompleted: number | null;
  unit: string;
  phase: string;
  trade: string;
  serviceProviderIres: string | null;
  serviceProviderErp: string | null;
  serviceProviderCompleted: string | null;
  completedByTimestamp: string | null;
  completedByUserId: string | null;
}

/** Section 11: Affected component dimensions */
export interface PdeAffectedComponent {
  apartmentNumber: number;
  roomNumber: number;
  component: string;
  /** A wall's sketch name ("Wand A"); null without a sketch and for floors and ceilings. */
  wallNumber: string | null;
  componentWidth: number;
  componentLength: number;
  componentHeight: number;
  componentArea: number;
  damageWidth: number;
  damageLength: number;
  damageHeight: number;
  damageArea: number;
}

/** Complete PDE document with all sections */
export interface PdeDocument {
  "0_projectInfo": PdeProjectInfo[];
  "1_apartments": PdeApartment[];
  "2_rooms": PdeRoom[];
  "3_consolidatedWorkItems": PdeConsolidatedWorkItem[];
  "4_materials": PdeMaterial[];
  "5_timeEstimate": PdeTimeEstimate[];
  "6_timeTracking": PdeTimeTracking[];
  "8_deviceAssignments": PdeDeviceAssignment[];
  "10_workItems": PdeWorkItem[];
  "11_affectedComponents": PdeAffectedComponent[];
}

/** All known PDE section keys in display order */
export const PDE_SECTION_KEYS: (keyof PdeDocument)[] = [
  "0_projectInfo",
  "1_apartments",
  "2_rooms",
  "3_consolidatedWorkItems",
  "4_materials",
  "5_timeEstimate",
  "6_timeTracking",
  "8_deviceAssignments",
  "10_workItems",
  "11_affectedComponents",
];
